import { useEffect, useMemo, useState } from "react"

import NumberFlow from "@number-flow/react"
import { formatNumber } from "accounting"
import { Flex, Space, Spin, Typography } from "antd"
import cls from "classnames"
import { t } from "i18next"
import { useDispatch, useSelector } from "react-redux"

import ButtonComponent from "../../../../../Components/Button"
import { getCompareParams } from "../../../../../Components/Filters/CompareSelect/presets"
import FAQModal from "../../../../../Components/Modal/FAQModal"
import Private from "../../../../../Components/Private"
import SelectComponent from "../../../../../Components/Select"
import { useAnimatedNumber } from "../../../../../hooks/useAnimatedNumber"
import Icon from "../../../../../Icon"
import Images from "../../../../../Images"
import { getDates } from "../../../../../Utils/utils"
import { useDemoDataToggle } from "../../../../User/hooks/useDemoDataToggle"
import HealthActions from "../../../reducer"
import { useVampHistoricalDataMode } from "../../hooks/useHistoricalDataMode"
import { useVampDataAvailable } from "../../hooks/useVampDataAvailable"
import InfoLine from "../InfoLine/InfoLine"
import MainChart from "../MainChart/MainChart"
import SectionHeader from "../SectionHeader/SectionHeader"
import { vampFAQ } from "./vampFAQ"

import "./MainChartBlock.less"

const { Text } = Typography

const totalBlocksStyles = {
  vamp_rate: {
    title: "VAMP Rate",
    color: "#35BD29",
    isDashed: true
  },
  fraud_rate: {
    title: "Fraud Rate",
    color: "#225AEA"
  },
  dispute_rate: {
    title: "Dispute Rate",
    color: "#DF7009"
  },
  vamp_zone: {
    title: "VAMP Zone",
    color: "#D71313"
  }
}

const MainChartBlock = ({ filters, business_account_id }) => {
  const dispatch = useDispatch()
  const { isShowDemoData } = useDemoDataToggle()
  const { vampMainChart, vampMainChartFetching, descriptor, descriptorsList, mainInfo } =
    useSelector((state) => state.health)
  const { isHistoricalDataMode } = useVampHistoricalDataMode()
  const isVampDataAvailable = useVampDataAvailable()
  const { isMobile } = useSelector((state) => state.nav)
  const [dataType, setDataType] = useState("rate")
  const [isFAQOpen, setIsFAQOpen] = useState(false)

  const descriptorOptions = useMemo(() => {
    return descriptorsList?.map((desc) => ({
      label: desc,
      value: desc
    }))
  }, [descriptorsList])

  useEffect(() => {
    if (filters?.period && descriptor && isVampDataAvailable) {
      const period_query = getDates(filters.period)
      const compare_query = getCompareParams(filters?.compare, period_query)
      dispatch(
        HealthActions.getVampMainChartRequest({
          ...period_query,
          ...compare_query,
          business_account: business_account_id,
          descriptor,
          ...(isHistoricalDataMode ? { historical_data: true } : {})
        })
      )
    }
  }, [
    filters,
    business_account_id,
    isShowDemoData,
    descriptor,
    isHistoricalDataMode,
    isVampDataAvailable
  ])

  const totalBlocks = useMemo(() => {
    const compBlocks = vampMainChart?.comparison?.blocks_data
    const compLabel = vampMainChart?.comparison_period?.label
    const withComp = (value, comparisonValue) => ({
      value,
      comparisonValue,
      comparisonLabel: compLabel
    })
    if (dataType === "rate")
      return [
        {
          ...withComp(vampMainChart?.blocks_data?.vamp_rate, compBlocks?.vamp_rate),
          ...totalBlocksStyles.vamp_rate
        },
        {
          ...withComp(vampMainChart?.blocks_data?.fraud_rate, compBlocks?.fraud_rate),
          ...totalBlocksStyles.fraud_rate
        },
        {
          ...withComp(vampMainChart?.blocks_data?.dispute_rate, compBlocks?.dispute_rate),
          ...totalBlocksStyles.dispute_rate
        },
        {
          value: vampMainChart?.threshold?.rate,
          ...totalBlocksStyles.vamp_zone
        }
      ]
    if (dataType === "count")
      return [
        {
          ...withComp(vampMainChart?.blocks_data?.fraud_count, compBlocks?.fraud_count),
          ...totalBlocksStyles.fraud_rate
        },
        {
          ...withComp(vampMainChart?.blocks_data?.dispute_count, compBlocks?.dispute_count),
          ...totalBlocksStyles.dispute_rate
        },
        {
          value: vampMainChart?.threshold?.count,
          ...totalBlocksStyles.vamp_zone
        }
      ]
  }, [vampMainChart, dataType])

  const updateDescriptor = (newDescriptor) => {
    dispatch(HealthActions.healthSetProp("descriptor", newDescriptor))
  }

  const infoLineText = `For the best health of your business - we strictly recommend to keep your VAMP rate under the ${
    mainInfo?.vamp_region ? vampMainChart?.threshold?.rate + "%" : "-"
  } line. It will help to keep your business safe.`

  return (
    <div
      className={cls("health-monitor-main-chart-block", {
        "is-mobile": isMobile
      })}
    >
      <Spin spinning={vampMainChartFetching}>
        <SectionHeader
          title="Visa Acquirer Monitoring Program (VAMP)"
          image={Images.visa_3d}
          marginBottom={16}
        />
        <Flex
          gap={20}
          align="flex-start"
          justify="space-between"
          className="main-chart-legend"
          wrap="wrap-reverse"
        >
          <Flex gap={36} wrap="wrap" className="main-chart-legend__items">
            {totalBlocks.map((block) => (
              <TotalBlock key={block.title} block={block} dataType={dataType} />
            ))}
          </Flex>
          <Flex gap={8} className="main-chart-legend__controls">
            <ButtonComponent
              size={"large"}
              icon={<Icon type={"question_help"} />}
              onClick={() => setIsFAQOpen(true)}
            >
              What is VAMP?
            </ButtonComponent>
            <SelectComponent
              showSearch
              value={descriptor}
              onChange={updateDescriptor}
              options={descriptorOptions}
              className="monitoring-descriptors-select"
              popupMatchSelectWidth={false}
              placement="bottomRight"
            />
            <Space.Compact size={"large"}>
              <ButtonComponent
                className={cls("compact-btn", {
                  active: dataType === "rate"
                })}
                style={{
                  borderRadius: "12px 0 0 12px",
                  minWidth: 66
                }}
                onClick={() => setDataType("rate")}
              >
                {t("Rate")}
              </ButtonComponent>
              <ButtonComponent
                className={cls("compact-btn", {
                  active: dataType === "count"
                })}
                style={{
                  minWidth: 66
                }}
                onClick={() => setDataType("count")}
              >
                {t("Count")}
              </ButtonComponent>
            </Space.Compact>
          </Flex>
        </Flex>
        <MainChart data={vampMainChart} dataType={dataType} />
        <InfoLine text={infoLineText} variant="neutral" style={{ marginTop: "16px" }} />
      </Spin>
      <FAQModal
        open={isFAQOpen}
        close={() => setIsFAQOpen(false)}
        data={vampFAQ}
        title={"Learn more about Visa Acquirer Monitoring Program"}
      />
    </div>
  )
}

const TotalBlock = ({ block, dataType }) => {
  const animatedValue = useAnimatedNumber(block.value)
  // Incognito mode hides the rates; the field names always stay, and the Count view keeps its
  // dispute / fraud counts readable (CHA1-1357).
  const isRate = dataType === "rate"
  return (
    <Flex gap={8} align="flex-start" className="chart-legend">
      <div className={cls(`legend-color`, { dashed: block.isDashed })}>
        <div
          className={cls(`color`, { dashed: block.isDashed })}
          style={{
            background: block.isDashed
              ? `repeating-linear-gradient(to right, ${block.color} 0 4px, transparent 4px 8px)`
              : block.color
          }}
        />
      </div>
      <Flex gap={4} vertical>
        <Text type="secondary" strong>
          {block.title}
        </Text>
        <Text strong className="chart-total-value">
          {block.value || block.value === 0 ? (
            <Private disabled={!isRate}>
              <NumberFlow
                value={animatedValue}
                suffix={isRate ? "%" : ""}
                format={
                  isRate
                    ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                    : { maximumFractionDigits: 0 }
                }
              />
            </Private>
          ) : (
            "-"
          )}
        </Text>
        {block.comparisonValue != null && block.comparisonLabel && (
          <Text type="secondary" className="chart-total-comparison">
            <Private disabled={!isRate}>
              {isRate
                ? `${formatNumber(block.comparisonValue, 2, ",", ".")}%`
                : formatNumber(block.comparisonValue, 0, ",", ".")}
            </Private>{" "}
            {block.comparisonLabel}
          </Text>
        )}
      </Flex>
    </Flex>
  )
}

export default MainChartBlock
